import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { apiRequest } from "../api";
import { BriefingDoc, Contribution, Output } from "../types/dbTypes";
import { useDatadog } from "./use-datadog";
import { useGlobal } from "./use-global";
import { useAccount } from "./use-account";

interface RoadMapDatesType {
  peak_ambition_date: string;
  three_year: string;
  one_year: string;
  next_quarter: string;
  next_quarter_number: number;
}

export const useRoadMapProvider = () => {
  const { trackError } = useDatadog();
  const { view } = useGlobal();

  const [activeBlock, setActiveBlock] = useState<string>(null);
  const { companyName } = useAccount();

  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [headlineImgUrl, setHeadlineImgUrl] = useState<string>(null);

  const [roadmapDates, setRoadmapDates] = useState<RoadMapDatesType>(null);

  const [versionIndex, setVersionIndex] = useState<number>(-1);
  const [briefingDoc, setBriefingDoc] = useState<BriefingDoc>(undefined);
  const [isLoadingBriefingDoc, setIsLoadingBriefingDoc] =
    useState<boolean>(false);

  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [isContributionsLoading, setIsContributionsLoading] =
    useState<boolean>(false);

  const getContributions = async () => {
    setIsContributionsLoading(true);
    const data = await apiRequest<Contribution[]>({
      subRoute: "roadmap",
      path: `/get_contributions?building_block=${activeBlock}&include_deselected=true`,
      method: "GET"
    });

    setContributions(data);
    setIsContributionsLoading(false);
  };

  const toggleContributionSelection = async (
    contributionId: string,
    isSelected: boolean
  ) => {
    try {
      await apiRequest({
        subRoute: "roadmap",
        path: `/toggle_contribution_selection`,
        method: "POST",
        body: {
          building_block: activeBlock,
          contribution_id: contributionId,
          is_selected: isSelected
        }
      });

      // Update local state
      setContributions((prev) =>
        prev.map((c) =>
          c.id === contributionId ? { ...c, is_deselected: !isSelected } : c
        )
      );
    } catch (error) {
      trackError("error_toggling_contribution_selection", error);
    }
  };

  const getRoadmapDates = async () => {
    const response = await apiRequest<RoadMapDatesType>({
      subRoute: "roadmap",
      path: `/get_dates?team_mode=${view === "team"}`,
      method: "GET"
    });

    setRoadmapDates(response);
  };

  const getHeadlineImgUrl = async (topicId: string) => {
    if (!imageLoading) {
      const response = await apiRequest<{
        public_url: string;
        is_generating: boolean;
      }>({
        subRoute: "topic",
        path: `/get_image?topic_id=${topicId}`
      });

      if (response) {
        if (response.is_generating) {
          setImageLoading(true);
          setTimeout(() => {
            getHeadlineImgUrl(topicId);
          }, 5000);
        } else {
          setImageLoading(false);
          setHeadlineImgUrl(response.public_url);
        }
      } else {
        setHeadlineImgUrl(null);
      }
    }
  };

  const generateBriefingDoc = async () => {
    setIsLoadingBriefingDoc(true);

    await apiRequest<BriefingDoc>({
      subRoute: "roadmap",
      path: `/generate_briefing_doc`,
      method: "POST",
      body: {
        building_block: activeBlock
      }
    });

    await getBriefingDoc();
  };

  const getBriefingDoc = useCallback(async () => {
    setIsLoadingBriefingDoc(true);

    const data = await apiRequest<BriefingDoc>({
      subRoute: "roadmap",
      path: `/get_briefing_doc?building_block=${activeBlock}`,
      method: "GET"
    });

    setBriefingDoc(data);

    setIsLoadingBriefingDoc(false);
  }, [activeBlock]);

  const briefingDocOutputs = useMemo(() => {
    if (!briefingDoc) {
      return null;
    } else {
      return briefingDoc.outputs;
    }
  }, [briefingDoc]);

  useEffect(() => {
    if (briefingDocOutputs?.length > 0) {
      setVersionIndex(briefingDocOutputs?.length - 1);
    } else {
      setVersionIndex(-1);
    }
  }, [briefingDocOutputs]);

  const currentBriefingDoc = useMemo(() => {
    if (
      briefingDocOutputs?.length > 0 &&
      versionIndex >= 0 &&
      versionIndex < briefingDocOutputs.length
    ) {
      return briefingDocOutputs[versionIndex];
    }

    return null;
  }, [versionIndex, briefingDocOutputs]);

  const latestBriefingDocOutput = useMemo(() => {
    if (briefingDocOutputs?.length > 0) {
      return briefingDocOutputs[briefingDocOutputs.length - 1] as Output;
    }

    return null;
  }, [versionIndex, briefingDocOutputs]);

  const latestContributions = useMemo(() => {
    if (!latestBriefingDocOutput?.additional_data?.contributions) {
      return null;
    } else {
      return latestBriefingDocOutput.additional_data
        .contributions as Contribution[];
    }
  }, [latestBriefingDocOutput]);

  const newContributions = useMemo(() => {
    return contributions.filter((c) => {
      return (
        !latestContributions ||
        !latestContributions.find((bdc) => bdc.id === c.id)
      );
    });
  }, [latestContributions, contributions]);

  const updatedContributions = useMemo(() => {
    return contributions.filter((c) => {
      const bdCont = latestContributions?.find((bdc) => bdc.id === c.id);
      return !!bdCont && bdCont.text !== c.text;
    });
  }, [latestContributions, contributions]);

  useEffect(() => {
    getContributions();
  }, [activeBlock, view, companyName]);

  return {
    headlineImgUrl,
    setHeadlineImgUrl,
    getHeadlineImgUrl,

    imageLoading,
    setImageLoading,

    roadmapDates,

    getRoadmapDates,

    isLoadingBriefingDoc,

    generateBriefingDoc,
    briefingDoc,
    currentBriefingDoc,
    latestBriefingDocOutput,

    contributions,
    toggleContributionSelection,

    isContributionsLoading,

    newContributions,
    updatedContributions,

    versionIndex,
    setVersionIndex,

    latestContributions,

    getBriefingDoc,

    activeBlock,
    setActiveBlock
  };
};

export const RoadMapProvider = ({ children }) => {
  const roadmapProvider = useRoadMapProvider();

  return (
    <RoadMapContext.Provider value={roadmapProvider}>
      {children}
    </RoadMapContext.Provider>
  );
};

export type RoadMapProps = ReturnType<typeof useRoadMapProvider>;
export const RoadMapContext = createContext<RoadMapProps>(null);
export const useRoadMap = () => useContext(RoadMapContext);
